/* eslint-disable @faire/enforce-internal-folder-encapsulation */
import { Core, Decorative } from "@faire/design-tokens";
import { IProduct } from "@faire/web-api/indigofair/data/IProduct";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization/localize";
import { formatMoney } from "@faire/web/common/money";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import * as React from "react";
import { useMemo } from "react";
import styled from "styled-components";

import { ExpectedShipDate } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/ExpectedShipDate";
import { ProductName } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/ProductName";
import { BrandPageProductTileSwatches } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/ProductTileSwatches";
import { RetailInfoContainer } from "@faire/retailer/features/MarketplaceItems/styled";
import { showProductPromotions } from "@faire/retailer/lib/showProductPromotions";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { Price } from "@faire/retailer/views/product/ProductDetails/Price/Price";
import { PRICE_TEXT_SURFACE } from "@faire/retailer/views/product/ProductDetails/Price/PriceProps";

import { ProductTileInfo } from "./ProductTileInfo";

interface IPropTypes {
  productTile: ProductTileInfo;
  isLoggedInOrBrandPreview: boolean;
  hideWholesalePrice?: boolean;
  hidePricing?: boolean;
  priceInlineNoTruncate?: boolean;
  isSalesPaused: boolean;
  showOrderVolume?: boolean;
}

export const RetailInfo: React.FC<IPropTypes> = (props) => {
  const {
    isLoggedInOrBrandPreview: isLoggedIn,
    hidePricing,
    productTile,
    hideWholesalePrice,
    isSalesPaused,
  } = props;

  const limitedStockText = useMemo(() => {
    const availableUnits =
      productTile.quick_add?.quick_add_option?.option_available_units ??
      Number.POSITIVE_INFINITY;
    const unitMultiplier =
      productTile.quick_add?.quick_add_option?.option_unit_multiplier ?? 1;

    const comesInCases = unitMultiplier !== 1;
    const availableQuantity = comesInCases
      ? Math.floor(availableUnits / unitMultiplier)
      : availableUnits;

    const isLimitedStock = availableQuantity > 0 && availableQuantity <= 3;

    const isSalesPaused =
      productTile.product?.state === IProduct.State.SALES_PAUSED;

    if (!isLimitedStock || isSalesPaused) {
      return null;
    }

    return comesInCases ? (
      <LimitedStock>
        <StrictLocalMsg
          defaultMessage="{availableQuantity, plural, one{Only # case left!} other{Only # cases left!}}"
          description={{
            text: "There are only X amount of product cases left in stock. The amount of product cases that the brand has in their inventory.",
          }}
          values={DEPRECATED_backwardsCompatibleValues({ availableQuantity })}
        />
      </LimitedStock>
    ) : (
      <LimitedStock>
        <StrictLocalMsg
          defaultMessage="Only {availableQuantity} left!"
          description={{
            text: "There are only X amount of products left in stock. The amount of products that the brand has in their inventory.",
          }}
          values={DEPRECATED_backwardsCompatibleValues({ availableQuantity })}
        />
      </LimitedStock>
    );
  }, [
    productTile.quick_add?.quick_add_option?.option_available_units,
    productTile.quick_add?.quick_add_option?.option_unit_multiplier,
    productTile.product?.state,
  ]);

  const eligibleForInventoryText =
    isLoggedIn &&
    (productTile.case_size_text || limitedStockText || isSalesPaused);

  const productLevelPromotion = !productTile?.is_locked_view
    ? productTile?.min_option_wholesale_promo_price
    : undefined;

  const originalPrice = productTile?.min_option_wholesale_price
    ? formatMoney({
        money: productTile.min_option_wholesale_price,
        omitCentsIfZero: true,
      })
    : "";

  const wholesalePrice = productTile?.min_option_wholesale_promo_price
    ?.discounted_item_price
    ? formatMoney({
        money:
          productTile.min_option_wholesale_promo_price.discounted_item_price,
        omitCentsIfZero: true,
      })
    : originalPrice;

  const retailPrice = productTile?.min_option_retail_price
    ? formatMoney({
        money: productTile.min_option_retail_price,
        omitCentsIfZero: true,
      })
    : "";

  const { isUK } = useRetailerStore(["isUK"]);
  return (
    <>
      <RetailInfoContainer>
        <ProductName productName={productTile.product?.name ?? ""} />
        <Spacer height={4} />
        {hidePricing ? null : (
          <Price
            isUKRetailer={isUK}
            showProductPromotions={showProductPromotions(productLevelPromotion)}
            isTilePricing
            small
            isLoggedInOrBrandPreview={isLoggedIn}
            originalPrice={originalPrice}
            wholesalePrice={wholesalePrice}
            hideWholesalePrice={hideWholesalePrice}
            retailPrice={retailPrice}
            priceTextSurface={PRICE_TEXT_SURFACE.BRAND}
          />
        )}
        {eligibleForInventoryText ? (
          <CaseLine>
            {productTile.case_size_text ? (
              <>
                <Typography
                  variant="paragraphSansRegular"
                  color={Core.text.subdued}
                >
                  {productTile.case_size_text}
                </Typography>
                <Spacer width="1x" />
              </>
            ) : null}
            <StockTextContent
              limitedStockText={limitedStockText}
              isSalesPaused={isSalesPaused}
            />
          </CaseLine>
        ) : null}
        {productTile && productTile.options_details ? (
          <>
            <Spacer size={{ mobileAndAbove: 2, tabletAndAbove: 4 }} />
            <BrandPageProductTileSwatches productTileInfo={productTile} />
          </>
        ) : null}
        <ExpectedShipDate shipDate={productTile.expected_shipping_date} />
      </RetailInfoContainer>
    </>
  );
};

interface IStockTextProps {
  limitedStockText: React.ReactNode;
  isSalesPaused: boolean;
}

const StockTextContent: React.FC<IStockTextProps> = (props) => {
  const { limitedStockText, isSalesPaused } = props;

  if (limitedStockText) {
    return <>{limitedStockText}</>;
  } else if (isSalesPaused) {
    return (
      <Typography variant="paragraphSansRegular" color={Core.text.critical}>
        <StrictLocalMsg
          defaultMessage="Out of stock"
          description={{
            text: "This product is currently unavailable.",
          }}
        />
      </Typography>
    );
  } else {
    return null;
  }
};

const LimitedStock = styled(Typography).attrs({
  color: Decorative.shade.orange,
})`
  width: auto;
  font-style: italic;
`;

const CaseLine = styled(Row).attrs({ align: "baseline" })`
  width: 100%;
  flex-wrap: wrap;
`;
